import { Search, QrCode, ChevronLeft, ChevronRight, Play, Pause, ArrowRight, Scan, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { getDemoProfile } from "@/utils/demoProfile";
import { optimizeCloudinaryImage } from "@/utils/imageOptimizer";
import { containsNSFW } from "@/hooks/useDebouncedValidation";
import { StickerPacksDrawer } from "@/components/stickers/StickerPacksDrawer";

interface AboutFindOwnerSectionProps {
  searchType: "vehicleNumber" | "metroCard" | "metefyToken" | "username";
  setSearchType: Dispatch<SetStateAction<"vehicleNumber" | "metroCard" | "metefyToken" | "username">>;
  searchValue: string;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  isSearching: boolean;
  searchResult: any;
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  pageKey?: 'index' | 'findowner' | 'vehicle';
  mode?: 'vehicle' | 'metro';
  openDemoProfile?: (profile: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    x_username: string | null;
    bio: string | null;
    metro_card_number: string | null;
    created_at: string;
    updated_at: string;
    show_email: boolean;
    show_mobile: boolean;
    metefy_token: string | null;
    photo_url?: string | null;
    mask_bio?: boolean;
    social_sms?: string | null;
    social_call?: string | null;
    social_telegram?: string | null;
    social_whatsapp?: string | null;
    use_social_icons?: boolean;
  }) => void;
}

type SearchType = "vehicleNumber" | "metroCard" | "metefyToken" | "username";

export function AboutFindOwnerSection({
  searchType,
  setSearchType,
  searchValue,
  setSearchValue,
  handleSearch: parentHandleSearch,
  isSearching: parentIsSearching,
  searchResult,
  isDialogOpen,
  setIsDialogOpen,
  pageKey = 'index',
  mode = 'vehicle',
  openDemoProfile
}: AboutFindOwnerSectionProps) {
  const { toast } = useToast();
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isStickerDrawerOpen, setIsStickerDrawerOpen] = useState(false);
  const isVehicleMode = mode === 'vehicle';
  const secondaryDefault = isVehicleMode ? "metroCard" : "vehicleNumber";
  const [secondaryValue, setSecondaryValue] = useState<SearchType>(secondaryDefault);
  const secondaryOptions = useMemo(
    () =>
      isVehicleMode
        ? [
            { value: "metroCard" as SearchType, label: "Metro Card" },
            { value: "metefyToken" as SearchType, label: "Other Products" },
            { value: "username" as SearchType, label: "Username" },
          ]
        : [
            { value: "vehicleNumber" as SearchType, label: "Vehicle" },
            { value: "metefyToken" as SearchType, label: "Other Products" },
            { value: "username" as SearchType, label: "Username" },
          ],
    [isVehicleMode]
  );

  useEffect(() => {
    setSecondaryValue(secondaryDefault);
  }, [secondaryDefault]);

  useEffect(() => {
    if (secondaryOptions.some((option) => option.value === searchType)) {
      setSecondaryValue(searchType);
    }
  }, [searchType, secondaryOptions]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Original image URLs
  const originalStickerImages = [
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_apdspu.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627368/Site_Posts_2_ast368.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627361/Site_Posts_7_xzu6fl.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627365/Site_Posts_3_ovezi7.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627375/Site_Posts_1_gypuwl.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627365/Site_Posts_8_dgqyme.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_9_twcxeh.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627363/Site_Posts_6_qvodxi.png"
  ];

  // Optimize images with Cloudinary transformations (600px width, 80% quality, auto format)
  const stickerImages = useMemo(() => {
    return originalStickerImages.map(url => optimizeCloudinaryImage(url, 600, 80, 'auto'));
  }, []);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % stickerImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + stickerImages.length) % stickerImages.length);
  };

  // Minimum swipe distance (in px) to trigger a swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    if (isPlaying) return;
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (isPlaying) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (isPlaying || !touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  // Preload all images on component mount
  useEffect(() => {
    stickerImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const secureHandleSearch = async () => {
    if (!searchValue) {
      toast({
        title: "Please enter a number",
        description: searchType === "vehicleNumber"
          ? "Enter the vehicle number to find its owner"
          : searchType === "metroCard" 
          ? "Enter your metro card number to find its owner" 
          : "Enter your Metefy token number to find its owner",
        variant: "destructive",
      });
      return;
    }

    if (searchType === "vehicleNumber") {
      const vehicleNumber = searchValue.trim().toUpperCase();
      if (vehicleNumber.length < 6 || vehicleNumber.length > 15) {
        toast({
          title: "Invalid vehicle number",
          description: "Vehicle number should be between 6 and 15 characters",
          variant: "destructive",
        });
        return;
      }
      setSearchValue(vehicleNumber);
    } else if (searchType === "metroCard" && searchValue.length < 4) {
      toast({
        title: "Invalid metro card number",
        description: "Metro card number should be at least 4 digits",
        variant: "destructive",
      });
      return;
    }

    if (searchType === "metefyToken" && searchValue.length < 12) {
      toast({
        title: "Invalid Metefy token",
        description: "Metefy token should be 12 digits",
        variant: "destructive",
      });
      return;
    }

    if (searchType === "username") {
      const trimmedUsername = searchValue.trim().toLowerCase();
      if (trimmedUsername.length < 3 || trimmedUsername.length > 30) {
        toast({
          title: "Invalid username",
          description: "Username must be between 3 and 30 characters",
          variant: "destructive",
        });
        return;
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(trimmedUsername)) {
        toast({
          title: "Invalid username",
          description: "Username can only contain letters, numbers, underscores, and hyphens",
          variant: "destructive",
        });
        return;
      }
      // Check for NSFW content
      if (containsNSFW(trimmedUsername)) {
        toast({
          variant: "destructive",
          title: "NSFW Content Detected",
          description: "This username contains inappropriate words. Please use appropriate language.",
        });
        return;
      }
      setSearchValue(trimmedUsername);
    }

    // Optimistic dialog
    setIsDialogOpen(true);

    parentHandleSearch();
  };

  const handleOpenDemoProfile = () => {
    if (!openDemoProfile) return;
    openDemoProfile(getDemoProfile());
  };

  return (
    <section id="find-owner" className="pt-16 pb-8 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg text-foreground font-semibold text-sm mb-6">
            <Search className="w-4 h-4 text-foreground" />
            <span className="text-foreground">Find Owner</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Find the <span className="text-primary">Owner</span> Instantly
          </h2>
          {/* stats under heading removed as requested */}
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="flex flex-col items-center gap-3 w-full mb-6">
            <label 
              htmlFor="product-type" 
              className="text-lg font-semibold text-foreground"
            >
              Whose owner do you want to find?
            </label>
            <div className="flex gap-4 w-full justify-center items-center" id="product-type">
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="primaryOption"
                  name="searchType"
                  value={isVehicleMode ? "vehicleNumber" : "metroCard"}
                  checked={searchType === (isVehicleMode ? "vehicleNumber" : "metroCard")}
                  onChange={() => {
                    setSearchType(isVehicleMode ? "vehicleNumber" : "metroCard");
                    setSearchValue("");
                  }}
                  className="h-5 w-5 text-primary border-border focus:ring-0"
                />
                <label htmlFor="primaryOption" className="flex items-center gap-2 text-foreground font-medium">
                  <span>{isVehicleMode ? "Vehicle" : "Metro Card"}</span>
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="secondaryOption"
                  name="searchType"
                  value={secondaryValue}
                  checked={secondaryOptions.some((option) => option.value === searchType)}
                  onChange={() => {
                    const defaultValue = secondaryOptions[0]?.value ?? secondaryValue;
                    setSecondaryValue(defaultValue);
                    setSearchType(defaultValue);
                    setSearchValue("");
                  }}
                  className="h-5 w-5 text-primary border-border focus:ring-0"
                />
                <label htmlFor="secondaryOption" className="flex items-center gap-2 text-foreground font-medium">
                  <span>
                    {secondaryOptions.find((option) => option.value === secondaryValue)?.label || "Other Products"}
                  </span>
                </label>
                <Select
                  value={
                    secondaryOptions.some((option) => option.value === secondaryValue)
                      ? secondaryValue
                      : secondaryOptions[0]?.value
                  }
                  onValueChange={(value) => {
                    const nextValue = value as SearchType;
                    setSecondaryValue(nextValue);
                    setSearchType(nextValue);
                    setSearchValue("");
                  }}
                >
                  <SelectTrigger 
                    className="w-auto h-auto p-0 border-0 bg-transparent focus:ring-0 shadow-none hover:bg-transparent [&>span]:hidden [&>svg:last-child]:hidden"
                    aria-label={`Select ${secondaryOptions.find((option) => option.value === secondaryValue)?.label || "other product type"}`}
                  >
                    <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  </SelectTrigger>
                  <SelectContent>
                    {secondaryOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-center mb-8">
            <div className="flex-1 max-w-md">
              <Label htmlFor="search-input" className="sr-only">
                {searchType === "vehicleNumber"
                  ? "Vehicle Number"
                  : searchType === "metroCard"
                  ? "Metro Card Number"
                  : searchType === "metefyToken"
                  ? "Metefy Token Number"
                  : "Username"}
              </Label>
              <Input
                id="search-input"
                name="search-input"
                placeholder={
                  searchType === "vehicleNumber"
                    ? "Enter Vehicle Number..."
                    : searchType === "metroCard"
                    ? "Enter Metro Card Number..."
                    : searchType === "metefyToken"
                    ? "Enter 12 digit Metefy Token..."
                    : "Enter Username..."}
                value={
                  searchType === "metefyToken" && searchValue
                    ? searchValue.replace(/(\d{4})(?=\d)/g, '$1-')
                    : searchValue
                }
                onChange={(e) => {
                  if (searchType === "vehicleNumber") {
                    const val = e.target.value.replace(/[^A-Za-z0-9\s]/g, "").toUpperCase();
                    setSearchValue(val.length > 15 ? val.slice(0, 15) : val);
                  } else if (searchType === "username") {
                    let val = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
                    // Check for NSFW content
                    if (containsNSFW(val)) {
                      toast({
                        variant: "destructive",
                        title: "NSFW Content Detected",
                        description: "This username contains inappropriate words. Please use appropriate language.",
                      });
                      return; // Don't update if NSFW detected
                    }
                    setSearchValue(val.length > 30 ? val.slice(0, 30) : val);
                  } else {
                  let val = e.target.value.replace(/\D/g, "");
                  if (searchType === "metroCard") {
                    if (val.length > 16) val = val.slice(0, 16);
                  } else {
                    if (val.length > 12) val = val.slice(0, 12);
                  }
                  setSearchValue(val);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !parentIsSearching) {
                    e.currentTarget.blur();
                    secureHandleSearch();
                  }
                }}
                className="w-full bg-card border-2 border-primary focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 text-foreground placeholder:text-foreground/60"
                maxLength={
                  searchType === "vehicleNumber"
                    ? 15
                    : searchType === "metroCard"
                    ? 16
                    : searchType === "metefyToken"
                    ? 14
                    : 30
                }
                minLength={
                  searchType === "vehicleNumber"
                    ? 6
                    : searchType === "metroCard"
                    ? 4
                    : searchType === "metefyToken"
                    ? 12
                    : 3
                }
                inputMode={searchType === "vehicleNumber" || searchType === "username" ? "text" : "numeric"}
                pattern={
                  searchType === "vehicleNumber"
                    ? undefined
                    : searchType === "metroCard"
                    ? "\\d{4,16}"
                    : searchType === "metefyToken"
                    ? "\\d{4}-\\d{4}-\\d{4}"
                    : undefined
                }
                autoComplete="off"
                aria-label={
                  searchType === "vehicleNumber"
                    ? "Vehicle Number"
                    : searchType === "metroCard"
                    ? "Metro Card Number"
                    : searchType === "metefyToken"
                    ? "Metefy Token Number"
                    : "Username"
                }
              />
            </div>
            <Button
              onClick={secureHandleSearch}
              disabled={parentIsSearching}
              className="bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 px-4"
              aria-label={parentIsSearching ? "Searching..." : `Search for ${searchType === "vehicleNumber" ? "vehicle owner" : searchType === "metroCard" ? "metro card owner" : searchType === "metefyToken" ? "Metefy token owner" : "username owner"}`}
            >
              {parentIsSearching ? (
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" aria-hidden="true" />
              ) : (
                <Search className="w-4 h-4" aria-hidden="true" />
              )}
            </Button>
          </div>

          {/* Buy Stickers and Try Demo Buttons */}
          <div className="flex justify-center gap-4 mb-8">
            <button 
              onClick={() => setIsStickerDrawerOpen(true)}
              className="bg-foreground hover:bg-foreground/90 text-background px-4 py-2 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2 group cursor-pointer"
            >
              <QrCode className="w-4 h-4 group-hover:scale-110 transition-transform" />
              Buy Stickers
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={handleOpenDemoProfile}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2 group cursor-pointer"
            >
              <Scan className="w-4 h-4 group-hover:scale-110 transition-transform" />
              Try Demo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Commented out original cards for future use
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {searchType === 'metefyToken' ? (
              <>
                <button 
                  className={`relative flex items-center justify-center h-64 w-full group cursor-pointer ${(searchType as string) === 'metefyToken' ? 'order-first' : 'order-last'} md:order-none`}
                  onClick={() => {
                    setSearchType('metefyToken');
                    setSearchValue('');
                  }}
                >
                  <div className={`relative z-10 flex flex-col items-start justify-between ${(searchType as string) === "metefyToken" ? "bg-white border-sky-700 shadow-2xl" : "bg-white border-gray-400"} border-4 rounded-2xl shadow-xl px-8 py-6 w-80 h-44 transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl group-hover:border-sky-700`}>
                    <div className="flex flex-col items-start justify-center gap-4">
                      <div className="flex items-start">
                        <span className="font-bold text-lg text-foreground tracking-wide">
                          Other Products
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-0.5">
                        <span className="block font-bold text-base tracking-widest text-foreground">
                          Metefy Token
                        </span>
                        <span className="block text-sm font-mono tracking-widest text-foreground">
                          1234-5678-9012
                        </span>
                      </div>
                    </div>
                    <img 
                      src="/assets/stickers/metefytoken.svg"
                      alt="Metefy Token Example"
                      className={`absolute right-2 top-1/2 -translate-y-1/2 w-32 h-auto object-contain transition-transform duration-300 ${(searchType as string) === "metefyToken" ? "scale-110" : ""} group-hover:scale-110`}
                      loading="eager"
                      width="128"
                      height="128"
                    />
                  </div>
                </button>

                <button 
                  className={`relative flex items-center justify-center h-64 w-full group cursor-pointer ${(searchType as string) === 'metroCard' ? 'order-first' : 'order-last'} md:order-none`}
                  onClick={() => {
                    setSearchType('metroCard');
                    setSearchValue('');
                  }}
                >
                  <div className={`relative z-10 flex flex-col items-start justify-between ${(searchType as string) === "metroCard" ? "bg-white border-sky-700 shadow-2xl" : "bg-white border-gray-400"} border-4 rounded-2xl shadow-xl px-8 py-6 w-80 h-44 transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl group-hover:border-sky-700`}>
                    <div className="flex flex-col items-start justify-center gap-4">
                      <div className="flex items-start">
                        <span className="font-bold text-lg text-foreground tracking-wide">
                          Delhi Metro
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-0.5">
                        <span className="block font-bold text-base tracking-widest text-foreground">
                          Card Number
                        </span>
                        <span className="block text-lg font-mono tracking-widest text-foreground">
                          8443 5233
                        </span>
                      </div>
                    </div>
                    <img 
                      src="/assets/stickers/metrocard2.svg"
                      alt="Metro Card Example"
                      className={`absolute right-4 top-1/2 -translate-y-1/2 w-32 h-auto object-contain transition-transform duration-300 ${(searchType as string) === "metroCard" ? "scale-110" : ""} group-hover:scale-110`}
                      loading="eager"
                      width="128"
                      height="128"
                    />
                  </div>
                </button>
              </>
            ) : (
              <>
                <button 
                  className={`relative flex items-center justify-center h-64 w-full group cursor-pointer ${(searchType as string) === 'metroCard' ? 'order-first' : 'order-last'} md:order-none`}
                  onClick={() => {
                    setSearchType('metroCard');
                    setSearchValue('');
                  }}
                >
                  <div className={`relative z-10 flex flex-col items-start justify-between ${(searchType as string) === "metroCard" ? "bg-white border-sky-700 shadow-2xl" : "bg-white border-gray-400"} border-4 rounded-2xl shadow-xl px-8 py-6 w-80 h-44 transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl group-hover:border-sky-700`}>
                    <div className="flex flex-col items-start justify-center gap-4">
                      <div className="flex items-start">
                        <span className="font-bold text-lg text-foreground tracking-wide">
                          Delhi Metro
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-0.5">
                        <span className="block font-bold text-base tracking-widest text-foreground">
                          Card Number
                        </span>
                        <span className="block text-lg font-mono tracking-widest text-foreground">
                          8443 5233
                        </span>
                      </div>
                    </div>
                    <img 
                      src="/assets/stickers/metrocard2.svg"
                      alt="Metro Card Example"
                      className={`absolute right-4 top-1/2 -translate-y-1/2 w-32 h-auto object-contain transition-transform duration-300 ${(searchType as string) === "metroCard" ? "scale-110" : ""} group-hover:scale-110`}
                      loading="eager"
                      width="128"
                      height="128"
                    />
                  </div>
                </button>

                <button 
                  className={`relative flex items-center justify-center h-64 w-full group cursor-pointer ${(searchType as string) === 'metefyToken' ? 'order-first' : 'order-last'} md:order-none`}
                  onClick={() => {
                    setSearchType('metefyToken');
                    setSearchValue('');
                  }}
                >
                  <div className={`relative z-10 flex flex-col items-start justify-between ${(searchType as string) === "metefyToken" ? "bg-white border-sky-700 shadow-2xl" : "bg-white border-gray-400"} border-4 rounded-2xl shadow-xl px-8 py-6 w-80 h-44 transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl group-hover:border-sky-700`}>
                    <div className="flex flex-col items-start justify-center gap-4">
                      <div className="flex items-start">
                        <span className="font-bold text-lg text-foreground tracking-wide">
                          Other Products
                        </span>
                      </div>
                      <div className="flex flex-col items-start gap-0.5">
                        <span className="block font-bold text-base tracking-widest text-foreground">
                          Metefy Token
                        </span>
                        <span className="block text-sm font-mono tracking-widest text-foreground">
                          1234-5678-9012
                        </span>
                      </div>
                    </div>
                    <img 
                      src="/assets/stickers/metefytoken.svg"
                      alt="Metefy Token Example"
                      className={`absolute right-2 top-1/2 -translate-y-1/2 w-32 h-auto object-contain transition-transform duration-300 ${(searchType as string) === "metefyToken" ? "scale-110" : ""} group-hover:scale-110`}
                      loading="eager"
                      width="128"
                      height="128"
                    />
                  </div>
                </button>
              </>
            )}
          </div>
          */}

          {/* Sticker Showcase Carousel */}
          <div className="mt-12 relative">
            {/* Fixed height container to prevent layout shift */}
            <div className="h-[300px] overflow-hidden relative">
              {/* Play/Pause Toggle - fixed position */}
              <button
                onClick={togglePlayPause}
                className="absolute bottom-4 right-4 z-20 bg-card/90 hover:bg-card border border-border text-foreground p-2 rounded-full shadow-lg transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-primary/50"
                aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
              </button>

              {/* Navigation Arrows (visible when paused) */}
              {!isPlaying && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-card/90 hover:bg-card border border-border text-foreground p-2 rounded-full shadow-lg transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-primary/50"
                    aria-label="Previous sticker"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  
                  <button
                    onClick={nextImage}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-card/90 hover:bg-card border border-border text-foreground p-2 rounded-full shadow-lg transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-primary/50"
                    aria-label="Next sticker"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {isPlaying ? (
                // Animated carousel when playing - reduced duplication for better performance
                <div className="flex animate-scroll h-full">
                  {[
                    // Duplicate only twice for seamless infinite loop (reduced from 20x to 2x)
                    ...stickerImages,
                    ...stickerImages,
                  ].map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`Metefy Sticker Design ${(index % stickerImages.length) + 1}`}
                      className="w-[300px] h-[300px] object-contain mx-4 rounded-lg flex-shrink-0"
                      width="300"
                      height="300"
                      loading={index < 2 ? "eager" : "lazy"}
                      decoding="async"
                    />
                  ))}
                </div>
              ) : (
                // Manual navigation when paused
                <div 
                  className="flex justify-center items-center h-full touch-pan-y"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  <div className="w-[300px] h-[300px] relative mx-4">
                    {stickerImages.map((src, index) => (
                      <img
                        key={index}
                        src={src}
                        alt={`Metefy Sticker Design ${index + 1}`}
                        className={`w-[300px] h-[300px] object-contain rounded-lg transition-opacity duration-300 select-none absolute top-0 left-0 ${
                          index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                        }`}
                        width="300"
                        height="300"
                        loading={index === 0 ? "eager" : "lazy"}
                        decoding="async"
                        draggable={false}
                        fetchPriority={index === 0 ? "high" : "auto"}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Dots Indicator (visible when paused) - fixed position */}
            <div className="h-8 flex items-center justify-center">
              {!isPlaying && (
                <div className="flex gap-2">
                  {stickerImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-primary scale-110' 
                          : 'bg-muted hover:bg-muted-foreground/50'
                      }`}
                      aria-label={`Go to sticker ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
      
      <StickerPacksDrawer isOpen={isStickerDrawerOpen} setIsOpen={setIsStickerDrawerOpen} openDemoProfile={openDemoProfile} />
    </section>
  );
}